<template>
  <b-row>
    <b-col lg="3">
      <b-form-group
        label="Basic Details"
        label-class="dine-in-table-creation-section-labels py-0"
      >
        <small class="sub-section-labels">Please provide details</small>
      </b-form-group>
    </b-col>
    <b-col lg="5">
      <b-form-group
        label="Table Name"
        label-class="dine-in-table-creation-labels"
      >
        <b-form-input
          id="tableName"
          :maxlength="maxLengths.table_name"
          v-model="form.table_name"
        >
        </b-form-input>
        <b-form-invalid-feedback :force-show="formValidatedState('table_name')">
          <div
            class="text-danger"
            v-for="(error, errorIndex) of getErrorMessage('table_name')"
            :key="`error-table-name-${errorIndex}`"
          >
            {{ error.$message }}
          </div>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        label="Description"
        label-class="dine-in-table-creation-labels"
      >
        <b-form-textarea
          name="description"
          id="description"
          class="form-control"
          :rows="3"
          :maxlength="maxLengths.description"
          v-model="form.description"
          placeholder="For your own reference, place notes here to help differentiate this table group from others"
        >
        </b-form-textarea>
        <div class="d-flex justify-content-between">
          <b-form-invalid-feedback
            :force-show="formValidatedState('description')"
          >
            <div
              class="text-danger"
              v-for="(error, errorIndex) of getErrorMessage('description')"
              :key="`error-description-${errorIndex}`"
            >
              {{ error.$message }}
            </div>
          </b-form-invalid-feedback>
          <small
            :class="[
              'flex-shrink-0 counter-container mt-1 text-right',
              { 'w-100': !formValidatedState('description') },
            ]"
          >
            {{ descriptionCount }} / {{ maxLengths.description }}
          </small>
        </div>
      </b-form-group>
      <BrandBranches
        @applyBrandsAndBranches="applyBrandsAndBranches"
        errorKey="branch_uuid"
        :brandBranch="form.branch_uuid"
        formGroupLabel="Branch"
        formGroupClass="secondary-label dine-in-table-creation-labels mb-2"
        :bypassMaxWidth="true"
        :isSingle="true"
        :maxWidthBypass="true"
      />
      <b-form-invalid-feedback
        :force-show="formValidatedState('branch_uuid')"
        class="mb-3"
      >
        <div
          class="text-danger"
          v-for="(error, errorIndex) of getErrorMessage('branch_uuid')"
          :key="`error-branch-uuids-${errorIndex}`"
        >
          {{ error.$message }}
        </div>
      </b-form-invalid-feedback>
      <b-form-group
        label="Area in Branch"
        label-class="dine-in-table-creation-labels"
        class="mt-3"
      >
        <b-form-input
          id="areaName"
          v-model="form.area_name"
          :maxlength="maxLengths.area_name"
          placeholder="Indicate table location within the branch. Examples: 1/F, Veranda, Private Room"
        >
        </b-form-input>
        <b-form-invalid-feedback :force-show="formValidatedState('area_name')">
          <div
            class="text-danger"
            v-for="(error, errorIndex) of getErrorMessage('area_name')"
            :key="`error-floor-area-name-${errorIndex}`"
          >
            {{ error.$message }}
          </div>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        label="Number of tables"
        label-class="dine-in-table-creation-labels"
      >
        <div class="position-relative">
          <b-form-input
            v-model="form.no_of_tables"
            autocomplete="off"
            :maxlength="maxLengths.no_of_tables"
            inputmode="numeric"
            pattern="[0-9]"
            @keyup="stripNonNumeric($event, 'no_of_tables')"
            @keydown="stripNonNumeric($event, 'no_of_tables')"
            @focus.native="isShowTableCountDropdown = true"
            @blur.native="handleTableCountBlur"
            @keypress.native="handleKeypress"
            id="tableCount"
            class="inputdropdown-text cursor-pointer"
          >
          </b-form-input>
          <div
            id="options-container"
            class="border rounded bg-white position-absolute w-100 shadow"
            v-if="isShowTableCountDropdown && tableCountOptions.length"
          >
            <div
              v-for="(count, countIndex) in tableCountOptions"
              :key="`${count}-count-${countIndex}`"
              class="px-3 py-2 cursor-pointer"
              @click.self="handleChangeTableCount(count)"
            >
              {{ count }}
            </div>
          </div>
        </div>
        <b-form-invalid-feedback
          :force-show="formValidatedState('no_of_tables')"
        >
          <div
            class="text-danger"
            v-for="(error, errorIndex) of getErrorMessage('no_of_tables')"
            :key="`error-table-count-${errorIndex}`"
          >
            {{ error.$message }}
          </div>
        </b-form-invalid-feedback>
      </b-form-group>
    </b-col>
  </b-row>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "@vue/composition-api";
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength, helpers } from "@vuelidate/validators";
import BrandBranches from "@/components/store/BrandBranches";
import _ from "lodash";

export default defineComponent({
  components: { BrandBranches },
  setup(props, { root, emit }) {
    const branches = computed(() => root.$store.state.branches.branches || []);
    const tableData = computed(() => root.$store.state.storeDineIn.tableData);

    const apiErrors = computed(
      () => root.$store.state.storeDineIn.errors || {}
    );
    const maxLengths = {
      table_name: 50,
      description: 500,
      no_of_tables: 3,
      area_name: 50,
    };

    const form = ref({
      table_name: null,
      description: null,
      branch_uuid: null,
      area_name: null,
      no_of_tables: null,
    });

    const descriptionCount = computed(() =>
      form.value.description ? form.value.description.length : 0
    );

    const tableCountTextDisplay = computed(() =>
      form.value.no_of_tables ? form.value.no_of_tables.toString() : " "
    );

    const tableCountOptions = computed(() =>
      Array(100)
        .fill(1)
        .map((n, index) => n + index)
        .filter((value) =>
          form.value.no_of_tables
            ? value.toString().includes(form.value.no_of_tables.toString())
            : value
        )
    );

    const isShowTableCountDropdown = ref(false);

    const alphanumericAndSomeSpecialCharacters =
      /^[A-Za-z0-9!';$".,&:*@?/#<([\-+=>)\]\s”’]*$/;

    const rules = {
      table_name: {
        required: helpers.withMessage("Table name is required.", required),
        maxLength: helpers.withMessage(
          `Table name may only contain ${maxLengths.table_name} characters.`,
          maxLength(maxLengths.table_name)
        ),
        mustBeAlphanumericAndSomeSpecialCharacters: helpers.withMessage(
          "Table name may only contain alphanumeric and some special characters.",
          (table_name) =>
            !helpers.req(table_name) ||
            alphanumericAndSomeSpecialCharacters.test(table_name)
        ),
      },
      description: {
        mustBeAlphanumericAndSomeSpecialCharacters: helpers.withMessage(
          "Description may only contain alphanumeric and some special characters.",
          (description) =>
            !helpers.req(description) ||
            alphanumericAndSomeSpecialCharacters.test(description)
        ),
        maxLength: helpers.withMessage(
          `Description may only contain ${maxLengths.description} characters.`,
          maxLength(maxLengths.description)
        ),
      },
      branch_uuid: {
        required: helpers.withMessage("Location is required.", required),
      },
      area_name: {
        required: helpers.withMessage("Area is required.", required),
        maxLength: helpers.withMessage(
          `Area may only contain ${maxLengths.area_name} characters.`,
          maxLength(maxLengths.area_name)
        ),
        mustBeAlphanumericAndSomeSpecialCharacters: helpers.withMessage(
          "Area may only contain alphanumeric and some special characters.",
          (area_name) =>
            !helpers.req(area_name) ||
            alphanumericAndSomeSpecialCharacters.test(area_name)
        ),
      },
      no_of_tables: {
        required: helpers.withMessage(
          "Number of tables is required.",
          required
        ),
        valueRequirement: helpers.withMessage(
          "Number of tables must be between 1-100.",
          (no_of_tables) =>
            !helpers.req(no_of_tables) ||
            (no_of_tables >= 1 && no_of_tables <= 100)
        ),
      },
    };

    const v$ = useVuelidate(rules, form);

    const formValidatedState = (field) => {
      return !!apiErrors.value[field] || v$.value[field].$error;
    };

    const getErrorMessage = (field) => {
            if (
        !_.isEmpty(apiErrors.value) &&
        _.has(apiErrors.value, field) &&
        apiErrors.value[field]
      ) {
        return [
          {
            $message: apiErrors.value[field],
          },
        ];
      } else {
        return v$.value[field].$errors;
      }
    };

    const applyBrandsAndBranches = (brandAndBranch) => {
      if (!_.isEmpty(brandAndBranch)) form.value.branch_uuid = brandAndBranch;
      else form.value.branch_uuid = null;
    };

    const setFormData = () => {
      const {
        table_name,
        description,
        area_name,
        branch_uuid,
        brand_uuid,
        no_of_tables,
      } = tableData.value;
      form.value = {
        table_name: table_name,
        description: description,
        branch_uuid: {
          branch_uuid: branch_uuid,
          brand_uuid: brand_uuid,
        },
        area_name: area_name,
        no_of_tables: no_of_tables,
      };
      setUntouchedForm();
    };

    const setUntouchedForm = () => {
      emit("setUntouchedForm", {
        basicDetails: JSON.stringify(form.value),
      });
    };

    onMounted(async () => {
      root.$on("validateTableCreation", () => v$.value.$touch());
      await root.$store.dispatch("branches/getBranches", true);
      if (tableData.value) setFormData();
      else setUntouchedForm();
    });

    onBeforeUnmount(() => {
      root.$off("validateTableCreation");
    });

    const isNotNumeric = (input) => {
      return /[^0-9\s]+/g.test(input);
    };

    const handleChangeTableCount = (count) => {
      form.value.no_of_tables = count.toString();
      nextTick(() => (isShowTableCountDropdown.value = false));
    };

    const handleKeypress = (e) => {
      if (isNotNumeric(String.fromCharCode(e.keyCode))) e.preventDefault();
    };

    const stripNonNumeric = (e, name) => {
      const backspaceKeyCode = 8;

      if (e.keyCode === backspaceKeyCode) return;

      form.value[name] = e.target.value.replace(/\D/g, "");
    };

    const handleTableCountBlur = () => {
      if (form.value.no_of_tables) v$.value.no_of_tables.$touch();
      setTimeout(() => {
        isShowTableCountDropdown.value = false;
      }, 300);
    };

    watch(
      () => tableData.value,
      () => {
        if (tableData.value) setFormData();
      }
    );

    watch(
      () => form.value.branch_uuid,
      () => {
        if (_.has(apiErrors.value, "branch_uuid")) {
          root.$store.commit("storeDineIn/emptyError", "branch_uuid");
        }
      }
    );

    watch(
      () => form.value.area_name,
      () => {
        if (_.has(apiErrors.value, "area_name")) {
          root.$store.commit("storeDineIn/emptyError", "area_name");
        }
      }
    );

    return {
      formValidatedState,
      maxLengths,
      v$,
      form,
      apiErrors,
      getErrorMessage,
      descriptionCount,
      applyBrandsAndBranches,
      branches,
      tableCountTextDisplay,
      tableCountOptions,
      isShowTableCountDropdown,
      handleChangeTableCount,
      handleKeypress,
      stripNonNumeric,
      handleTableCountBlur,
    };
  },
});
</script>

<style lang="scss" scoped>
#options-container {
  max-height: 200px;
  overflow-y: auto;
  z-index: 1;
}
.inputdropdown-text {
  background: url("../../../assets/images/caret-down.svg") no-repeat center
    right 10px;
  &.bg-disabled {
    background-color: #e9ebed;
  }
}
</style>
